import { addDays, endOfDay, format, parseISO, startOfDay } from 'date-fns';
import _ from 'lodash';
import { normalize } from 'normalizr';

import {
  CompletedUnitCommand,
  completedUnitCommandSchema,
} from '@sophocles/entities/CompletedUnitCommand/completedUnitCommandRecord';
import { mapListToRecords, normalizeList, websocketsHandlerFactory } from '@sophocles/entities/entityUtils';
import {
  markPendingCommandsAsCompleted,
  pendingUnitCommandSchema,
  receiveAndWipePendingUnitCommands,
} from '@sophocles/entities/PendingUnitCommand';
import apiClient from '@sophocles/http-common';
import { checkHttpStatus, genericErrorAction } from '@sophocles/utils';

/* Receive Actions */
export const RECEIVE_COMPLETED_UNIT_COMMANDS = 'RECEIVE_COMPLETED_UNIT_COMMANDS';
export function receiveCompletedUnitCommands(json) {
  return {
    type: RECEIVE_COMPLETED_UNIT_COMMANDS,
    completedUnitCommands: mapListToRecords(json, CompletedUnitCommand),
  };
}

/* Request Actions */
// starter request
export const REQUEST_COMPLETED_UNIT_COMMAND = 'REQUEST_COMPLETED_UNIT_COMMAND';
function requestCompletedUnitCommand(completedUnitCommand_id) {
  return {
    type: REQUEST_COMPLETED_UNIT_COMMAND,
    completedUnitCommand_id,
  };
}

export const REQUEST_RECENT_COMPLETED_UNIT_COMMANDS_FOR_SITE = 'REQUEST_RECENT_COMPLETED_UNIT_COMMANDS_FOR_SITE';
function requestRecentCompletedUnitCommandForSite(site_id) {
  return {
    type: REQUEST_RECENT_COMPLETED_UNIT_COMMANDS_FOR_SITE,
    site_id,
  };
}

export const REQUEST_ALL_UNIT_COMMANDS_FOR_SITE_WITH_RANGE = 'REQUEST_ALL_UNIT_COMMANDS_FOR_SITE';
function requestAllUnitCommandForSiteWithRange(site_id, start, end) {
  return {
    type: REQUEST_ALL_UNIT_COMMANDS_FOR_SITE_WITH_RANGE,
    site_id,
    start,
    end,
  };
}

/* Fetch Actions */
// starter fetch

export function fetchCompletedUnitCommand(completedUnitCommand_id) {
  return dispatch => {
    dispatch(requestCompletedUnitCommand(completedUnitCommand_id));
    return apiClient
      .get(`/completedcommands/${completedUnitCommand_id}/`)
      .then(checkHttpStatus)
      .then(response => normalizeList(response.data, 'completedUnitCommands', completedUnitCommandSchema))
      .then(json => dispatch(receiveCompletedUnitCommands(json.entities.completedUnitCommands)))
      .catch(error => {
        dispatch(genericErrorAction(error, REQUEST_COMPLETED_UNIT_COMMAND));
      });
  };
}

export function fetchRecentCompletedUnitCommandsForSite(site_id) {
  const oneDayAgo = format(addDays(new Date(), -1), 'yyyy-MM-dd');
  const oneDayAgoString = encodeURIComponent(oneDayAgo);

  return dispatch => {
    dispatch(requestRecentCompletedUnitCommandForSite(site_id));
    return apiClient
      .get(`/sites/${site_id}/commands/`, { params: { limit: 25, created_time__gte: oneDayAgoString } })
      .then(checkHttpStatus)
      .then(response => {
        const { results } = response.data;
        return {
          completed: _.get(
            normalizeList(results.completed, 'completedUnitCommands', completedUnitCommandSchema),
            ['entities', 'completedUnitCommands'],
            [],
          ),
          pending: _.get(
            normalizeList(results.pending, 'pendingUnitCommands', pendingUnitCommandSchema),
            ['entities', 'pendingUnitCommands'],
            [],
          ),
        };
      })
      .then(json => {
        const pending_pks = _.map(json.completed, command => command.pending_pk + '');
        dispatch(markPendingCommandsAsCompleted(pending_pks));
        dispatch(receiveAndWipePendingUnitCommands(json.pending));
        return dispatch(receiveCompletedUnitCommands(json.completed));
      })

      .catch(error => dispatch(genericErrorAction(error, REQUEST_RECENT_COMPLETED_UNIT_COMMANDS_FOR_SITE)));
  };
}

export function fetchAllCommandsForSiteWithRange(site_id, from, to) {
  const fromTS = startOfDay(parseISO(from));
  const toTS = endOfDay(parseISO(to));

  const fromString = encodeURIComponent(format(fromTS, 'yyyy-MM-dd'));
  const toString = encodeURIComponent(format(toTS, 'yyyy-MM-dd'));

  return dispatch => {
    dispatch(requestAllUnitCommandForSiteWithRange(site_id, format(fromTS, 'yyyy-MM-dd'), format(toTS, 'yyyy-MM-dd')));
    return apiClient
      .get(`/sites/${site_id}/commands/`, {
        params: { limit: 500, created_time__gte: fromString, created_time__lte: toString },
      })
      .then(checkHttpStatus)
      .then(response => ({
        completed: _.get(
          normalizeList(response.data.completed, 'completedUnitCommands', completedUnitCommandSchema),
          ['entities', 'completedUnitCommands'],
          [],
        ),
        pending: _.get(
          normalizeList(response.data.pending, 'pendingUnitCommands', pendingUnitCommandSchema),
          ['entities', 'pendingUnitCommands'],
          [],
        ),
      }))
      .then(json => {
        const pending_pks = _.map(json.completed, command => command.pending_pk + '');
        dispatch(markPendingCommandsAsCompleted(pending_pks));
        dispatch(receiveAndWipePendingUnitCommands(json.pending));
        return dispatch(receiveCompletedUnitCommands(json.completed));
      })
      .catch(error => dispatch(genericErrorAction(error, REQUEST_ALL_UNIT_COMMANDS_FOR_SITE_WITH_RANGE)));
  };
}
/* Edit Actions */
export const EDIT_COMPLETED_UNIT_COMMAND = 'EDIT_COMPLETED_UNIT_COMMAND';
export function editCompletedUnitCommand(completedUnitCommand_id) {
  return {
    type: EDIT_COMPLETED_UNIT_COMMAND,
    completedUnitCommand: completedUnitCommand_id,
  };
}

export function patchCompletedUnitCommand(completedUnitCommand_id, values) {
  return dispatch => {
    dispatch(editCompletedUnitCommand(completedUnitCommand_id));
    return apiClient
      .patch(`/completedcommands/${completedUnitCommand_id}/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, completedUnitCommandSchema))
      .then(json => {
        dispatch(receiveCompletedUnitCommands(json.entities.completedUnitCommands));
      })
      .catch(error => {
        dispatch(genericErrorAction(error, EDIT_COMPLETED_UNIT_COMMAND));
      });
  };
}

/* Create Actions */
export const CREATE_COMPLETED_UNIT_COMMAND = 'CREATE_COMPLETED_UNIT_COMMAND';
function sayCreateCompletedUnitCommand(values) {
  return {
    type: CREATE_COMPLETED_UNIT_COMMAND,
    values: values,
  };
}

export function createCompletedUnitCommand(values) {
  return dispatch => {
    dispatch(sayCreateCompletedUnitCommand(values));
    return apiClient
      .post(`/completedcommands/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, completedUnitCommandSchema))
      .then(json => {
        dispatch(receiveCompletedUnitCommands(json.entities.completedUnitCommands));
      })
      .catch(error => {
        dispatch(genericErrorAction(error, CREATE_COMPLETED_UNIT_COMMAND));
      });
  };
}

/* Delete Actions */
export const DELETE_COMPLETED_UNIT_COMMAND = 'DELETE_COMPLETED_UNIT_COMMAND';
function sayDeleteCompletedUnitCommand(completedUnitCommand_id) {
  return {
    type: DELETE_COMPLETED_UNIT_COMMAND,
    completedUnitCommand: completedUnitCommand_id,
  };
}

export function deleteCompletedUnitCommand(completedUnitCommand_id) {
  return dispatch =>
    apiClient
      .delete(`/completedcommands/${completedUnitCommand_id}/`)
      .then(checkHttpStatus)
      .then(r => dispatch(sayDeleteCompletedUnitCommand(completedUnitCommand_id)))
      .catch(error => {
        dispatch(genericErrorAction(error, DELETE_COMPLETED_UNIT_COMMAND));
      });
}
const receiver = (dispatch, json) => {
  // const pending_pks = json.entities.completedUnitCommands.map( (command) => command.get('pending_pk', null) + "" ).toArray();
  const pending_pks = _.map(json.entities.completedUnitCommands, command => command.pending_pk + '');
  dispatch(markPendingCommandsAsCompleted(pending_pks));
  return dispatch(receiveCompletedUnitCommands(json.entities.completedUnitCommands));
};

export const handleCompletedUnitCommandsSocketActions = websocketsHandlerFactory(
  receiver,
  sayDeleteCompletedUnitCommand,
  completedUnitCommandSchema,
);
